import { useState } from "react";
import { X } from "lucide-react";
import { THOUGHT_FILES } from "@/data/misc";
import { useSettings } from "@/os/state/settings";
import { play } from "@/lib/sound";

export function UnnecessaryThoughts() {
    const { sliders } = useSettings();
    const [openId, setOpenId] = useState<string | null>(null);
    const item = THOUGHT_FILES.find((f) => f.id === openId);

    return (
        <div className="relative" data-testid="unnecessary-thoughts">
            <p className="font-mono text-[11px] text-inkfaint">six fictional files. none of them open, all of them honest.</p>
            <div className="mt-3 space-y-1">
                {THOUGHT_FILES.map((f) => {
                    const Icon = f.icon;
                    return (
                        <button key={f.id} data-testid={`thought-file-${f.id}`} className="file-row" onClick={() => { play("click"); setOpenId(f.id); }}>
                            <Icon size={16} style={{ color: "#EC4899", flex: "none" }} />
                            <span className="truncate">{f.name}</span>
                            <span className="file-meta">{f.meta}</span>
                        </button>
                    );
                })}
            </div>

            {sliders.overthinking >= 70 && <DecisionSimulator />}

            {item && (
                <div className="absolute inset-0 z-30 grid place-items-center rounded-2xl bg-ink/30 p-3" data-testid="thought-popup">
                    <div className="pop-in max-h-full w-full overflow-auto os-scroll rounded-2xl border-2 border-ink bg-paper p-4 shadow-[5px_5px_0_#1C1917]">
                        <div className="flex items-center gap-2">
                            <item.icon size={15} style={{ color: "#EC4899", flex: "none" }} />
                            <p className="flex-1 truncate font-mono text-[12px] font-bold">{item.name}</p>
                            <button data-testid="thought-popup-close" aria-label="Close" className="text-inkfaint hover:text-ink" onClick={() => setOpenId(null)}>
                                <X size={15} />
                            </button>
                        </div>
                        {item.lines.map((l, i) => (
                            <p key={i} className="mt-2 text-[13px] leading-relaxed text-inksoft">
                                {l}
                            </p>
                        ))}
                        {sliders.overthinking >= 70 && (
                            <div className="mt-3 rounded-xl border-2 border-lav bg-lavsoft/50 p-2.5 font-mono text-[11px] leading-relaxed">
                                <span className="font-bold">alternative take (overthinking mode):</span> {item.altTake}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function DecisionSimulator() {
    const [a, setA] = useState("");
    const [b, setB] = useState("");
    const [verdict, setVerdict] = useState<string | null>(null);
    const [n, setN] = useState(0);

    const decide = () => {
        const t = n + 1;
        setN(t);
        const pick = (a.length * 7 + b.length * 13 + t * 31) % 2;
        const winner = pick === 0 ? a || "option A" : b || "option B";
        const note =
            t % 2 === 0
                ? "note: it chose the other one last time. both options were the same idea in different fonts."
                : "note: overthinking mode is not liable for outcomes. it is liable for the process.";
        setVerdict(`the simulator has weighed both options and chosen: "${winner}". confidence: 62%. regret: pending. ${note}`);
        play("click");
    };

    return (
        <div className="mt-5 rounded-2xl border-2 border-lav bg-lavsoft/40 p-4" data-testid="decision-simulator">
            <p className="font-display text-sm font-extrabold">decision simulator (overthinking mode)</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <input
                    data-testid="decision-option-a"
                    aria-label="Option A"
                    className="rounded-xl border border-sand bg-paper p-2 font-mono text-[12px]"
                    placeholder="option A"
                    value={a}
                    onChange={(e) => setA(e.target.value)}
                />
                <input
                    data-testid="decision-option-b"
                    aria-label="Option B"
                    className="rounded-xl border border-sand bg-paper p-2 font-mono text-[12px]"
                    placeholder="option B"
                    value={b}
                    onChange={(e) => setB(e.target.value)}
                />
            </div>
            <button data-testid="decision-run" className="btn btn-sm mt-3" onClick={decide}>
                decide for me
            </button>
            {verdict && (
                <p className="mt-3 font-mono text-[11.5px] leading-relaxed text-inksoft" data-testid="decision-verdict">
                    {verdict}
                </p>
            )}
        </div>
    );
}
